import { Burger, Divider, Stack, em, px, useMantineTheme } from '@mantine/core';
import type { MantineBreakpoint, MantineTheme } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useEffect, useRef } from 'react';
import {
  BANT_ISLERIM_ESIGI_PX,
  BANT_ROL_ESIGI,
  BANT_ROZET_ESIGI,
  KABUK_KIRILMA,
  MENU_SATIR_IC_PAY,
} from '../theme';

/**
 * DAR EKRAN KABUĞU — ÇEKMECE + BANT SADELEŞMESİ (görsel dil dilim 2,
 * 21.09.2026; `.claude/rules/gorsel-dil.md` §11).
 *
 * > **Web yüzeyi bir masaüstü ürünüdür; dar ekranda ölçüt "rahat" değil,
 * > "KIRIK DEĞİL"dir.**
 *
 * ⚠️ **NEDEN İKİ KABUK BU DOSYAYI PAYLAŞIR:** kırılma noktası ve menü
 * genişliği bugüne dek `AppShellLayout` ile `PortalShellLayout` içinde AYRI
 * AYRI literal olarak yazılıydı (`breakpoint: 'xs'` iki kez). Kopya tek
 * tarafta değişirse iki kabuk sessizce ayrışır ve kimse görmez — §2.31a'nın
 * (menü rol listesi beş kez ayrışmıştı) kabuk ölçeğindeki kardeşi. Davranış
 * burada TEK yerde yaşar; kabuklar yalnız kendi düğümlerini yerleştirir.
 *
 * ⚠️ **BİLGİ GİZLENMEZ, YER DEĞİŞTİRİR — VE DÜĞÜM ÇOĞALMAZ.** Rol etiketi,
 * holding rozeti ve *Çıkış yap* dar ekranda bandan çekmeceye **taşınır**;
 * aynı düğüm iki yerde birden çizilip biri CSS ile gizlenmez. Sebebi yalnız
 * zarafet değil: `data-testid` taşıyan bir düğümün iki kopyası e2e'de
 * ayrım gözetmeksizin eşleşir ve `ssoCikisi()` gibi **tek kapılı** yardımcılar
 * çalışamaz hâle gelir (`e2e/giris.ts` — çıkış düğmesine yalnız o basar).
 * Bu yüzden `visibleFrom`/`hiddenFrom` DEĞİL, ölçüye göre TEK düğüm.
 */

/**
 * `<sınır` medya sorgusu — Mantine'ın `hiddenFrom`uyla aynı kenar.
 * Sınır bir kırılma noktası ADI ya da doğrudan PİKSEL olabilir: bandın
 * kendi eşikleri ölçümden gelir ve hiçbir kırılma noktasına denk düşmez
 * (§11.1 — *"bant kendi ölçüsünü kendi taşır"*).
 */
function altinda(theme: MantineTheme, sinir: MantineBreakpoint | number): string {
  // `px()` dönüşü `number | string` tiplidir (girdiye göre) — sayıya zorlanır.
  const kenar = typeof sinir === 'number' ? sinir : Number(px(theme.breakpoints[sinir]));
  return `(max-width: ${em(kenar - 0.1)})`;
}

export interface KabukOlcusu {
  /** `KABUK_KIRILMA` altında: menü ÇEKMECE, bantta Burger. */
  readonly darEkran: boolean;
  /** `BANT_ROL_ESIGI` altında: rol etiketi + *Çıkış yap* çekmeceye iner. */
  readonly rolCekmecede: boolean;
  /** `BANT_ROZET_ESIGI` altında: holding rozeti de çekmeceye iner. */
  readonly cokDar: boolean;
  /** `BANT_ISLERIM_ESIGI_PX` altında: İşlerim düğmesi ikon + sayıya iner. */
  readonly kisaBant: boolean;
}

/**
 * Kabuğun ölçü durumu. İki eşik de `theme.ts`ten okunur — ekran kendi medya
 * sorgusunu YAZMAZ (§2.38'in ölçü kardeşi: değer tek kaynaktan).
 */
export function useKabukOlcusu(): KabukOlcusu {
  const theme = useMantineTheme();
  const secenek = { getInitialValueInEffect: false } as const;
  // ⚠️ `getInitialValueInEffect: false` BİLİNÇLİ: varsayılan davranış ilk
  // karede `undefined` döner, yani dar ekranda bant bir kare MASAÜSTÜ hâliyle
  // çizilip sonra sadeleşir — kullanıcı açılışta bandın kırık hâlini görür.
  const darEkran = useMediaQuery(altinda(theme, KABUK_KIRILMA), undefined, secenek);
  // ⚠️ MENÜNÜN EŞİĞİ İLE BANDIN EŞİKLERİ AYRI SORULARDIR (21.09.2026):
  // menünün eşiği bir CİHAZ SINIFI kararı, bandınkiler bandın kendi SIĞMA
  // ölçüsü. Dört bayrak iç içe geçer (kisaBant ⊂ cokDar ⊂ rolCekmecede ⊂
  // darEkran) ama tek bir değerden türemezler.
  const rolCekmecede = useMediaQuery(altinda(theme, BANT_ROL_ESIGI), undefined, secenek);
  const cokDar = useMediaQuery(altinda(theme, BANT_ROZET_ESIGI), undefined, secenek);
  const kisaBant = useMediaQuery(altinda(theme, BANT_ISLERIM_ESIGI_PX), undefined, secenek);
  return { darEkran, rolCekmecede, cokDar, kisaBant };
}

export interface Cekmece {
  readonly acik: boolean;
  readonly degistir: () => void;
  /** Kalem seçilince · Esc · dışarı tıklama · geniş ekrana dönüş. */
  readonly kapat: () => void;
  readonly navbarRef: React.RefObject<HTMLDivElement | null>;
  readonly burgerRef: React.RefObject<HTMLButtonElement | null>;
}

/**
 * Menü çekmecesi. Durum YERELDİR — genel durum deposu v1'de yoktur (§2.16) ve
 * bu bilgi hiçbir sunucu verisi taşımaz.
 */
export function useMenuCekmecesi(darEkran: boolean): Cekmece {
  const [acik, islem] = useDisclosure(false);
  const navbarRef = useRef<HTMLDivElement | null>(null);
  const burgerRef = useRef<HTMLButtonElement | null>(null);

  // Geniş ekrana dönüşte durum kapalıya iner: menü zaten sabit görünür, ama
  // "açık" kalan durum dar ekrana geri dönüldüğünde çekmeceyi kendiliğinden
  // açardı — kullanıcı açmadığı bir menüyle karşılaşırdı.
  useEffect(() => {
    if (!darEkran) islem.close();
  }, [darEkran, islem]);

  useEffect(() => {
    if (!acik) return;
    const tus = (olay: KeyboardEvent): void => {
      if (olay.key === 'Escape') islem.close();
    };
    const disari = (olay: PointerEvent): void => {
      const hedef = olay.target as Node | null;
      if (!hedef) return;
      if (navbarRef.current?.contains(hedef)) return;
      // Burger kendi tıklamasında zaten `degistir` çağırır; burada da
      // kapatsaydık iki kapatma üst üste gelir ve düğme AÇMAZ olurdu.
      if (burgerRef.current?.contains(hedef)) return;
      islem.close();
    };
    document.addEventListener('keydown', tus);
    document.addEventListener('pointerdown', disari);
    return () => {
      document.removeEventListener('keydown', tus);
      document.removeEventListener('pointerdown', disari);
    };
  }, [acik, islem]);

  return { acik, degistir: islem.toggle, kapat: islem.close, navbarRef, burgerRef };
}

/**
 * Bandın solunda, logonun ÖNÜNDE duran menü düğmesi. Yalnız dar ekranda
 * çizilir (kabuk `darEkran` ile karar verir — burada ikinci bir eşik yok).
 *
 * ⚠️ Renk token'dan: bant koyu lacivert zeminlidir ve Mantine'ın Burger'i açık
 * temada SİYAH çizer — varsayılanıyla bırakılsa düğme zeminin içinde kaybolur.
 */
export function MenuBurger({ cekmece }: { cekmece: Cekmece }): React.JSX.Element {
  return (
    <Burger
      ref={cekmece.burgerRef}
      opened={cekmece.acik}
      onClick={cekmece.degistir}
      size="sm"
      color="var(--isg-capa-uzeri-metin)"
      aria-label={cekmece.acik ? 'Menüyü kapat' : 'Menüyü aç'}
      aria-expanded={cekmece.acik}
      data-testid="menu-burger"
    />
  );
}

/** Çekmecenin üstü — banttan inen bağlam (holding adı · rol etiketi). */
export function CekmeceBaglami({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <Stack gap={6} px={MENU_SATIR_IC_PAY} pb="xs" data-testid="cekmece-baglami">
      {children}
      <Divider />
    </Stack>
  );
}

/** Çekmecenin altı — banttan inen *Çıkış yap*. Kaydırılan listenin DIŞINDA. */
export function CekmeceAlti({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <Stack gap="xs" pt="xs" data-testid="cekmece-alti">
      <Divider />
      {children}
    </Stack>
  );
}
